// Libs
import { ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Field, FormikProvider, useFormik } from 'formik';
import Grid from '@mui/material/Grid';
import moment from 'moment';

// Components
import { ButtonRow } from 'components/ButtonRow';
import { FormikDatePicker, FormikDropdown, FormikInputField } from 'components/forms';

// Store
import { updateUserProfile } from 'store/profile/actions';
import { userProfileSelector } from 'store/profile/selectors';
import { editGeneralValidation } from './edit-general-validation';
import { useTaxonomyMedicalSchools } from 'hooks';
import { DEFAULT_TIMEZONE, useAppDispatch } from 'core';

interface IProps {
  onClose: VoidFunction;
}

export const EditGeneral = ({ onClose }: IProps): ReactElement => {
  const dispatch = useAppDispatch();
  const userProfile = useSelector(userProfileSelector);
  const medicalSchools = useTaxonomyMedicalSchools();
  const initialValues = useMemo(() => {
    const { dobDate, dobMonth, ...rest } = userProfile;

    // preventing a leap year date, '02/29', from showing up as '02/28` instead once the datepicker gets rendered
    let newDate: Date;
    if (dobMonth === 2 && dobDate === 29) {
      newDate = moment.tz(`1904-${dobMonth}-${dobDate + 1}`, DEFAULT_TIMEZONE).toDate();
    } else {
      newDate = moment.tz(`1904-${dobMonth}-${dobDate}`, DEFAULT_TIMEZONE).toDate();
    }

    const dateOfBirth = dobMonth >= 1 && dobDate >= 1 && newDate;

    return { dob: dateOfBirth, ...rest };
  }, [userProfile]);

  const formik = useFormik({
    initialValues,
    onSubmit: async (values): Promise<void> => {
      const { dob, ...rest } = values;
      const dobMoment = moment(dob);
      const dobDate = dobMoment.date();
      const dobMonth = dobMoment.month() + 1;
      await dispatch(updateUserProfile({ dobDate, dobMonth, ...rest }));
      onClose();
    },
    validationSchema: editGeneralValidation,
  });

  const { handleSubmit, isSubmitting, isValid, dirty } = formik;

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormikInputField formikKey="firstName" label="First Name" required />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormikInputField formikKey="middleName" label="Middle Name" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormikInputField formikKey="lastName" label="Last Name" required />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Field name="dob">
              {({ field }) => (
                <FormikDatePicker
                  field={field}
                  format="MM/DD"
                  formikKey="dob"
                  label="Date of Birth"
                  openTo="day"
                  placeholder="MM/DD"
                  required
                  timeOfDay="midnight"
                />
              )}
            </Field>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormikInputField formikKey="alternateFirstName" label="Alternate First Name" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormikInputField formikKey="alternateLastName" label="Alternate Last Name" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormikInputField formikKey="zipCode" label="Home Zip Code" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormikDropdown
              formikKey="medicalSchoolId"
              label="Medical School"
              idProp="id"
              items={medicalSchools}
              labelProp="name"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormikInputField formikKey="phoneNumber" label="Phone Number" />
          </Grid>
        </Grid>
        <ButtonRow
          firstButtonText="Save"
          isPrimaryDisabled={isSubmitting || !isValid || !dirty}
          isSecondaryDisabled={isSubmitting}
          isShowProgress={isSubmitting}
          onCancel={onClose}
          secondButtonText="Cancel"
          type="submit"
        />
      </form>
    </FormikProvider>
  );
};
